import { useTranslation } from "react-i18next";
import {
  useForm,
  FormProvider,
  useWatch,
  useFormContext,
  Controller,
} from "react-hook-form";
import { Controllers } from "@/CommonElements/Controller";
import { Label, Spinner } from "reactstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  addQToItem,
  cn,
  handleFormErrors,
  PickKeyPairs,
} from "@/utils/helpers";
import { ActionsFooter } from "@/Component/ActionsFooter";
import {
  Cancel01Icon,
  FloppyDiskFreeIcons,
} from "@hugeicons/core-free-icons/index";
import {
  useAllAssignedInspectionUsers,
  useCreateHousingInspectionOrder,
  useEditHousingInspectionOrder,
  useOneHousingInspectionOrder,
  usePeriodCodeDetails,
  useSearchHousesQuery,
} from "@/services/houseInspections";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { AntSelect } from "@/CommonElements/AntSelect";
import { Button, Col, Row, Stack } from "react-bootstrap";
import { useCachedBasicData } from "@/utils/hooks/useCachedBasicData";
import { LabelValue } from "@/Component/Value";
import { Modal, ModalBody } from "reactstrap";
import { Checkbox, Divider, Flex } from "antd";
import { CancelIcon } from "@hugeicons/core-free-icons";
import { useListModal } from "@/utils/hooks/useListModal";
import { useHousingInspectionContext } from "./HousingInspectionContext/housingContext";
import { EXAMINATION_TYPES } from "@/utils/constants";
import { CountModal } from "@/Component/CountModal";
import { Info } from "@/Component/Info";
import { toast } from "react-toastify";
import { useSystem } from "@/utils/hooks/useLocation";

export const HousingInspectionOrderForm = ({
  fromBuildingsNotInspectedState,
} = {}) => {
  const navigate = useNavigate();
  // ==========
  // Hooks
  // ==========
  const [showExaminationPeriodsModal, setShowExaminationPeriodsModal] =
    useState(false);
  const [period_code, setPeriodCode] = useState(null);
  const { t } = useTranslation();
  const form = useForm();
  const params = useParams();
  const { house_contract_id: paramContractId, permit_no: paramPermitNo, mode } = params;
  const house_contract_id = paramContractId ?? fromBuildingsNotInspectedState?.contractId;
  const permit_no = paramPermitNo ?? fromBuildingsNotInspectedState?.permit_no;
  const isViewMode = mode === "view";
  const { getLocalizedText, getLocalizedNumber } = useLocalizedText();
  const { cachedBasicData } = useCachedBasicData();
  const examinationTypes = cachedBasicData?.examination_types || [];
  const examinationItems = cachedBasicData?.examination_items || [];
  const {
    control,
    formState: { errors },
  } = form;
  const {
    inspectionState: { selectedInspections, type },
    clearSelectedInspections,
  } = useHousingInspectionContext();
  const houseRef = useRef([]);
  const houses = houseRef?.current;
  const { currentSystem } = useSystem();
  // Modal hooks
  const examinationItemsModal = useListModal();
  const assignedUsersModal = useListModal();

  // ==========
  // Queries
  // ==========
  const {
    data: periodCodeDetailsRes,
    isLoading: isLoadingPeriodCodeDetails,
    isFetched,
  } = usePeriodCodeDetails(period_code);
  const periodCodeDetails = periodCodeDetailsRes?.data?.data || {};
  const examinationPeriods = useMemo(
    () => periodCodeDetails?.examination_periods || [],
    [periodCodeDetails]
  );
  const assigned_users = periodCodeDetails?.assigned_users || [];
  const { data: orderRes, isLoading: isLoadingOrder } =
    useOneHousingInspectionOrder();
  const orderData = useMemo(() => orderRes?.data?.data || {}, [orderRes]);
  const { data: assignedUsersRes, isLoading: isLoadingAssignedUsers } =
    useAllAssignedInspectionUsers({
      contract_id: house_contract_id,
      city: "makkah",
    });

  const fromBuildingsNotInspected = !!fromBuildingsNotInspectedState?.permit_no;
  const { data: searchHousesRes } = useSearchHousesQuery(
    fromBuildingsNotInspected
      ? { permit_no: fromBuildingsNotInspectedState.permit_no, house_contract_id }
      : { permit_no: "" }
  );
  const houseFromBuildingsNotInspected = useMemo(() => {
    const list = searchHousesRes?.data?.data?.data ?? searchHousesRes?.data?.data ?? [];
    const arr = Array.isArray(list) ? list : [];
    return arr.find(
      (h) => String(h?.permit_no ?? h?.permit_no) === String(fromBuildingsNotInspectedState?.permit_no)
    ) ?? arr[0];
  }, [searchHousesRes?.data?.data, fromBuildingsNotInspectedState?.permit_no]);

  const {
    mutate: createHousingInspectionOrder,
    isPending: isCreatingHousingInspectionOrder,
  } = useCreateHousingInspectionOrder(form.setError);

  const {
    mutate: editHousingInspectionOrder,
    isPending: isEditingHousingInspectionOrder,
  } = useEditHousingInspectionOrder();

  const isPending =
    isCreatingHousingInspectionOrder || isEditingHousingInspectionOrder;
  const assignedUsers = assignedUsersRes?.data?.data || [];
  const isTrack = type === EXAMINATION_TYPES.track || orderData?.examination_type?.value === EXAMINATION_TYPES.track;

  const allHouses = useMemo(() => {
    if (fromBuildingsNotInspected && houseFromBuildingsNotInspected) {
      return [{
        id: houseFromBuildingsNotInspected.id,
        permit_no: houseFromBuildingsNotInspected.permit_no,
        items: [],
      }];
    }
    if (mode !== "create" && orderData?.houses?.length) {
      return orderData.houses.map((h) => ({
        id: h.house_id ?? h.id,
        permit_no: h.permit_no,
        items: h.items || [],
      }));
    }
    if (selectedInspections?.length) {
      return selectedInspections.map((inspection) => ({
        id: inspection.house?.id ?? inspection.id,
        permit_no: inspection.house?.permit_no ?? inspection.permit_no,
        items: inspection.items || [],
      }));
    }
    return [];
  }, [fromBuildingsNotInspected, houseFromBuildingsNotInspected, selectedInspections, mode, orderData]);

  // Modal handlers
  const handleShowExaminationItems = (val) => {
    const items =
      examinationItems.filter((item) => val?.includes?.(item.id)) || [];
    const notFoundItems = val?.filter(
      (item) => !examinationItems.some((i) => i.id === item)
    );
    examinationItemsModal.openModal([
      ...items,
      ...notFoundItems.map((item) => String(item)),
    ]);
  };

  const handleShowAssignedUsers = (val) => {
    const users =
      assignedUsers.filter((user) => val?.includes?.(user.id)) || [];
    assignedUsersModal.openModal(users);
  };

  const onSubmit = (data) => {
    data.items = data.items?.map?.((item) => ({
      house_examination_item_id: item,
    }));

    const { selected_inspections, ...restPayload } = data;
    const getPermitNo = (id) =>
      allHouses.find((house) => house.id === id)?.permit_no ??
      (houseFromBuildingsNotInspected?.id === id
        ? (houseFromBuildingsNotInspected?.permit_no ??
           houseFromBuildingsNotInspected?.permit_no)
        : undefined);
    const payload = {
      ...restPayload,
      houses: selected_inspections?.map?.((id) => ({
        house_id: id,
        permit_no: getPermitNo(id),
      })),
    };

    if (mode === "edit") {
      editHousingInspectionOrder(
        { payload },
        {
          onSuccess: () => {
            setTimeout(() => {
              clearSelectedInspections();
            }, 1000);
            navigateToOrders();
          },
          onError: (error) => {
            handleFormErrors(
              error,
              form.setError,
              t,
              "HOUSING_INSPECTION_ORDER_EDITION_FAILED"
            );
          },
        }
      );
      return;
    }

    createHousingInspectionOrder(payload, {
      onSuccess: () => {
        form.reset({ items: [] });
        clearSelectedInspections();
        navigateToOrders();
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.message ||
          t("HOUSING_INSPECTION_ORDER_CREATED_FAILED")
        );
      },
    });
  };

  const navigateToOrders = () => {
    navigate(`/app/${currentSystem}/housing-inspection-orders`, {
      state: { newOrder: true },
    });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    form.handleSubmit(onSubmit)(e);
  };

  const openExaminationPeriodsModal = () => {
    setShowExaminationPeriodsModal(true);
  };

  const controllers = [
    {
      labelProps: {
        children: isTrack
          ? t("INSEPECTIONS")
          : t("SELECT_SELECTED_INSPECTIONS"),
        className: isTrack ? null : "required",
      },
      controllerProps: {
        name: "selected_inspections",
        render: ({ field }) => (
          <>
            {/* {allHouses.length > 0 && (
              <div className="mb-2 d-flex flex-wrap gap-2">
                {allHouses.map((house) => (
                  <span
                    key={house.id}
                    className="badge bg-light text-dark border"
                    style={{ fontSize: "0.875rem", padding: "0.5rem 0.75rem" }}
                  >
                    {house.permit_no || "-"}
                  </span>
                ))}
              </div>
            )} */}
            <div style={{ display: !isTrack ? "none" : "block" }}>
              <CountModal
                count={allHouses.length}
                modal={{
                  header: {
                    children: <h2 className="h3">{t("BUILDINGS")}</h2>,
                  },
                  body: {
                    children: (
                      <ul
                        style={{ padding: 0 }}
                        className="d-flex flex-column gap-2"
                      >
                        {allHouses.map((house) => (
                          <li key={house.id} className="border p-2 rounded">
                            {house.permit_no || "-"}
                          </li>
                        ))}
                      </ul>
                    ),
                  },
                }}
              >
                <div>
                  {allHouses[0]?.permit_no ?? allHouses?.length ?? 0}
                </div>
              </CountModal>
            </div>
            <AntSelect
              {...field}
              options={
                fromBuildingsNotInspected && houseFromBuildingsNotInspected
                  ? [
                      {
                        value: houseFromBuildingsNotInspected.id,
                        label:
                          houseFromBuildingsNotInspected.permit_no ??
                          houseFromBuildingsNotInspected.permit_no ??
                          houseFromBuildingsNotInspected.houseCommercialNameAr,
                      },
                    ]
                  : allHouses.map((house) => ({
                      value: house.id,
                      label: house.permit_no,
                    }))
              }
              style={{ display: isTrack ? "none" : "block" }}
              disabled={isTrack || fromBuildingsNotInspected}
              mode="multiple"
              maxTagCount="responsive"
              allowClear={!fromBuildingsNotInspected}
              placeholder={t("SELECT_SELECTED_INSPECTIONS")}
            />
          </>
        ),
      },
    },
    {
      labelProps: {
        children: t("ORDER_TYPE"),
        className: isTrack ? null : "required",
      },
      controllerProps: {
        name: "examination_type",
        render: ({ field }) => (
          <>
            <div style={{ display: !isTrack ? "none" : "block" }}>
              {getLocalizedText(
                examinationTypes.find((type) => type.value === field.value)
                  ?.label_ar,
                examinationTypes.find((type) => type.value === field.value)
                  ?.label_en
              )}
            </div>
            <SelectCachedOptions
              style={{ display: isTrack ? "none" : "block" }}
              optionKey="examination_types"
              filter={
                isTrack
                  ? undefined
                  : (examinationType) =>
                    examinationType.value !== EXAMINATION_TYPES.track
              }
              disabled={isTrack || fromBuildingsNotInspected}
              options={(examinationType) => ({
                value: examinationType.value,
                label: getLocalizedText(
                  examinationType.label_ar,
                  examinationType.label_en
                ),
              })}
              status={errors?.examination_period ? "error" : undefined}
              id="examination_type"
              name="examination_type"
              placeholder={t("ORDER_TYPE")}
              {...field}
              onChange={(value) => {
                field.onChange(value);
                if (value === EXAMINATION_TYPES.normal) {
                  form.setValue(
                    "items",

                    cachedBasicData.examination_items.map((item) => item.id),
                    { shouldDirty: true, shouldValidate: true }
                  );
                }
                if (value === EXAMINATION_TYPES.complaint) {
                  form.setValue("items", []);
                }
              }}
            />
          </>
        ),
      },
      renderValue: (val) => {
        const type = examinationTypes.find((type) => type.value === val);
        return (
          <LabelValue.Value
            loading={isLoadingOrder}
            value={getLocalizedText(type?.label_ar, type?.label_en)}
          />
        );
      },
    },
    {
      labelProps: {
        children: t("INSPECTION_ITEMS"),
        className: isTrack ? null : "required",
      },
      controllerProps: {
        name: "items",
        render: ({ field }) => {
          const examinationType = form.watch("examination_type");
          const isNormalExaminationType = examinationType === "normal";
          const trackedItems = allHouses?.flatMap((house) => house.items);

          return (
            <>
              {isTrack && (
                <CountModal
                  modal={{
                    header: {
                      children: t("UNAVAILABLE_INSPECTION_ITEMS"),
                    },
                    body: {
                      children: <ExaminationItems items={allHouses} />,
                    },
                  }}
                >
                  <Flex gap={4} align="center">
                    {!!trackedItems?.length && (
                      <Info
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        title={
                          <Stack>
                            <span>
                              {t("ITEMS_COUNT")}: {trackedItems?.length}
                            </span>
                            <span>
                              {t("INSEPECTION_COUNT")}: {allHouses.length}
                            </span>
                          </Stack>
                        }
                      />
                    )}
                    {!!trackedItems?.length && (
                      <div>
                        {trackedItems.length}
                      </div>
                    )}
                  </Flex>
                </CountModal>
              )}
              <SelectCachedOptions
                style={{
                  display: isTrack ? "none" : "block",
                }}
                disabled={isNormalExaminationType}
                onClick={(e) => {
                  !isNormalExaminationType && openExaminationPeriodsModal();
                }}
                open={false}
                loading={isLoadingOrder}
                optionKey="examination_items"
                mode="multiple"
                maxTagCount="responsive"
                allowClear
                options={(examinationItem) => ({
                  value: examinationItem.id,
                  label: getLocalizedText(
                    examinationItem.name_ar,
                    examinationItem.name_en
                  ),
                })}
                status={errors?.items ? "error" : undefined}
                id="items"
                name="items"
                placeholder={t("INSPECTION_ITEMS")}
                {...field}
              />
            </>
          );
        },
      },
      renderValue: (val) => {
        if (!val || !val.length) {
          return <span className="text-muted">-</span>;
        }

        const items =
          examinationItems.filter((item) => val?.includes?.(item.id)) || [];
        const notFoundItems = val?.filter(
          (item) => !examinationItems.some((i) => i.id === item)
        );
        const allItems = [
          ...items,
          ...notFoundItems.map((item) => String(item)),
        ];

        if (!allItems?.length) {
          return <span className="text-muted">-</span>;
        }

        const firstItem =
          typeof allItems[0] === "string"
            ? allItems[0]
            : getLocalizedText(allItems[0].name_ar, allItems[0].name_en);

        return (
          <a
            href=""
            className="H6 fw-bold text-truncate"
            onClick={(e) => {
              e.preventDefault();
              handleShowExaminationItems(val);
            }}
          >
            {firstItem}
            {allItems.length > 1 && (
              <small className="ms-1">
                +{getLocalizedNumber(allItems.length - 1)}
              </small>
            )}
          </a>
        );
      },
    },
    {
      labelProps: {
        children: t("HOUSING_INSPECTION_ORDER_CODE"),
        className: "required",
      },
      controllerProps: {
        name: "period_code_id",
        render: ({ field }) => (
          <SelectCachedOptions
            optionKey="period_codes"
            options={(periodCode) => ({
              value: periodCode.id,
              label: periodCode.code,
            })}
            status={errors?.period_code_id ? "error" : undefined}
            id="period_code_id"
            name="period_code_id"
            placeholder={t("HOUSING_INSPECTION_ORDER_CODE")}
            {...field}
            onChange={(value) => {
              field.onChange(value);
              setPeriodCode(value);
            }}
          />
        ),
      },
    },
    {
      labelProps: {
        children: t("ORDER_PERIOD"),
        className: "required",
      },
      controllerProps: {
        name: "examination_period_id",
        render: ({ field }) => (
          <AntSelect
            disabled={!isFetched}
            allowClear
            optionFilterProp="q"
            maxTagCount="responsive"
            showSearch
            loading={isLoadingPeriodCodeDetails}
            options={examinationPeriods.map(addQToItem).map((period) => ({
              q: period.q,
              value: period.id,
              label: getLocalizedText(period.name_ar, period.name_en),
            }))}
            status={errors?.examination_period_id ? "error" : undefined}
            id="examination_period_id"
            name="examination_period_id"
            placeholder={t("ORDER_PERIOD")}
            {...field}
          />
        ),
      },

      renderValue: (val) => {
        const period = examinationPeriods.find(
          (period) => period.value === val
        );
        return (
          <LabelValue.Value
            loading={isLoadingOrder}
            value={getLocalizedText(period?.label_ar, period?.label_en)}
          />
        );
      },
    },
    {
      labelProps: {
        children: t("ASSIGNED_USERS"),
        className: "required",
      },
      controllerProps: {
        name: "assigned_users",
        render: ({ field }) => (
          <AntSelect
            loading={isLoadingPeriodCodeDetails}
            disabled={!isFetched}
            allowClear
            optionFilterProp="q"
            mode="multiple"
            maxTagCount="responsive"
            showSearch
            options={assigned_users.map(addQToItem).map((user) => ({
              q: user.q,
              value: user.id,
              label: getLocalizedText(user.name_ar, user.name_en),
            }))}
            status={errors?.assigned_users ? "error" : undefined}
            id="assigned_users"
            name="assigned_users"
            placeholder={t("ASSIGNED_USERS")}
            {...field}
          />
        ),
      },
      renderValue: (val) => {
        if (!val || val.length === 0) {
          return <span className="text-muted">-</span>;
        }

        const users =
          assignedUsers.filter((user) => val?.includes?.(user.id)) || [];

        if (users.length === 0) {
          return <span className="text-muted">-</span>;
        }

        const firstUser = getLocalizedText(users[0].name_ar, users[0].name_en);

        return (
          <a
            href=""
            className="H6 fw-bold text-truncate"
            onClick={(e) => {
              e.preventDefault();
              handleShowAssignedUsers(val);
            }}
          >
            {firstUser}
            {users.length > 1 && (
              <small className="ms-1">
                +{getLocalizedNumber(users.length - 1)}
              </small>
            )}
          </a>
        );
      },
    },
  ];

  useEffect(() => {
    if (isTrack && !!allHouses.length) {
      houseRef.current = allHouses;
    }
    // if (selectedInspections.length === 0 && mode === "create") {
    //   return navigate("/app/makkah/housing-inspection-orders");
    // }
  }, [isTrack, allHouses]);

  useEffect(() => {
    if (houseFromBuildingsNotInspected?.id) {
      form.setValue("selected_inspections", [houseFromBuildingsNotInspected.id], {
        shouldValidate: true,
      });
    }
    if (fromBuildingsNotInspected) {
      form.setValue("examination_type", EXAMINATION_TYPES.complaint, {
        shouldValidate: true,
      });
      form.setValue("items", []);
    }
  }, [houseFromBuildingsNotInspected?.id, fromBuildingsNotInspected]);

  useEffect(() => {
    const examinationPeriod = examinationPeriods?.[0];
    if (!!examinationPeriod) {
      form.setValue("examination_period_id", examinationPeriod.id, {
        shouldDirty: true,
        shouldValidate: true,
      });

      form.setValue("assigned_users", []);
    }
  }, [examinationPeriods]);

  useEffect(() => {
    const initialData = PickKeyPairs(orderData, [
      "period_code",
      "examination_period",
      "examination_type",
      "assigned_users",
      "items",
    ]);

    const createOrderItems = initialData?.items?.map?.(
      (item) => item?.house_examination_item_id
    );
    const notAvailableTrackItems = allHouses?.some((h) => h.items?.length)
      ? allHouses?.flatMap((house) =>
          house?.items?.map?.((item) => item?.house_examination_item_id)
        )
      : createOrderItems;

    const selectedInspectionsValue =
      fromBuildingsNotInspected && houseFromBuildingsNotInspected?.id
        ? [houseFromBuildingsNotInspected.id]
        : allHouses.map((house) => house.id);
    const examinationTypeValue =
      fromBuildingsNotInspected
        ? EXAMINATION_TYPES.complaint
        : isTrack
          ? EXAMINATION_TYPES.track
          : initialData?.examination_type?.value;
    const itemsValue =
      fromBuildingsNotInspected
        ? []
        : isTrack
          ? notAvailableTrackItems
          : createOrderItems;
    const resetData = {
      house_contract_id,
      permit_no,
      // -=-
      items: itemsValue,
      examination_type: examinationTypeValue,
      examination_period_id: initialData?.examination_period?.id,
      period_code_id: initialData?.period_code?.id,
      assigned_users: initialData?.assigned_users?.map?.((user) => user.id),
      selected_inspections: selectedInspectionsValue,
    };

    form.reset(resetData);
  }, [orderData, PickKeyPairs, fromBuildingsNotInspected, houseFromBuildingsNotInspected?.id, selectedInspections]);

  const backTo = window.history.length > 1 ? -1 : "/app/house-inspections";
  return (
    <form onSubmit={handleFormSubmit}>
      <style>{`
  .ant-select-selector * {
    min-height: 48px !important;
    display: flex !important;
    align-items: center !important;
  }
`}</style>
      <Controllers
        isViewMode={isViewMode}
        watch={form.watch}
        isLoading={isLoadingOrder}
        {...{
          controllers: controllers.map((config) => ({
            ...config,
            labelProps: {
              ...config.labelProps,
              className: cn("fw-bold", config.labelProps.className),
            },
            controllerProps: {
              ...config.controllerProps,
              rules: {
                ...(config.controllerProps.rules || {}),
                required: t("FIELD_REQUIRED"),
              },
              control,
              errors,
            },
            layout: {
              colProps: { xs: 12, md: 3, lg: 4 },
            },
          })),
        }}
      />

      {!isViewMode && (
        <ActionsFooter>
          <Button type="submit" className="btn btn-primary" disabled={isPending}>
            {isPending ? <Spinner size="sm" /> : <>{mode === "create" ? t("CREATE") : t("UPDATE")}</>}
          </Button>
          <Link disabled={isPending} to={isPending ? undefined : backTo} type="button" className="btn btn-danger">
            <HugeiconsIcon size={16} icon={Cancel01Icon} />
            {t("CANCEL")}
          </Link>
        </ActionsFooter>
      )}

      <ExaminationItem
        onClose={() => setShowExaminationPeriodsModal(false)}
        open={showExaminationPeriodsModal}
        control={control}
        setValue={form.setValue}
      />

      {/* Examination Items Modal */}
      <ListModal
        open={examinationItemsModal.isOpen}
        onClose={examinationItemsModal.closeModal}
        title={t("عناصر الفحص")}
        items={examinationItemsModal.items}
      />

      {/* Assigned Users Modal */}
      <ListModal
        open={assignedUsersModal.isOpen}
        onClose={assignedUsersModal.closeModal}
        title={t("أعضاء الوردية")}
        items={assignedUsersModal.items}
      />
    </form>
  );
};

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */

// Simple List Modal Component
const ListModal = ({ open, onClose, title, items }) => {
  const { t } = useTranslation();
  const { getLocalizedText } = useLocalizedText();

  return (
    <Modal isOpen={open} toggle={onClose} size="md" centered>
      <ModalBody>
        <h5 className="text-center border-bottom border-1 pb-3 mb-3 fw-bold">
          {title}
        </h5>
        <Stack gap={2} className="pb-2 sticky-bottom">
          <Divider />
          <div>
            <Button onClick={onClose} className="btn-danger btn-xs">
              <HugeiconsIcon size={14} icon={CancelIcon} />
              {t("CLOSE")}
            </Button>
          </div>
        </Stack>
      </ModalBody>
    </Modal>
  );
};

export const ExaminationItems = (props) => {
  const { items } = props;
  const { getLocalizedText } = useLocalizedText();
  const { t } = useTranslation();

  return (
    <ul style={{ padding: 0 }} className="d-flex flex-column gap-2">
      {items?.map((item, index) => (
        <>
          <li key={item.id || item || index} className="border p-2 rounded">
            <Flex align="center">
              <span>{t("PERMIT_NUMBER")}:{" "}</span>
              <span>

                {typeof item === "string" ? item : item?.permit_no}
              </span>
            </Flex>
          </li>
          <li>
            <ul style={{ listStyle: "disc", paddingInlineStart: "20px" }}>
              {item.items.map((item) => (
                <li key={item.id}>
                  {getLocalizedText(item.item_name_ar, item.item_name_en)}
                </li>
              ))}
            </ul>
          </li>
        </>
      ))}
    </ul>
  );
};

const ExaminationItem = (props) => {
  const { open, onClose, setValue, control } = props;
  const watchedItems = useWatch({
    control,
    name: "items",
  });
  const watchedExaminationType = useWatch({
    control,
    name: "examination_type",
  });

  const isTrack = watchedExaminationType === EXAMINATION_TYPES.track;

  const { t } = useTranslation();
  const { getLocalizedText } = useLocalizedText();
  const { cachedBasicData } = useCachedBasicData();
  const form = useForm({
    defaultValues: {
      items: [],
    },
  });

  useEffect(() => {
    form.reset({
      items: watchedItems,
    });
  }, [watchedItems]);

  const onSubmit = (data) => {
    data.items = data.items?.filter?.((item) => !!item);
    setValue("items", data.items, { shouldDirty: true, shouldValidate: true });
    onClose();
  };
  const handleFormSubmit = (e) => {
    e.preventDefault();
    form.handleSubmit(onSubmit)(e);
  };

  return (
    <FormProvider {...form}>
      <Modal
        isOpen={open}
        toggle={onClose}
        size="md"
        centered
        keyboard={false}
        backdrop="static"
      >
        <ModalBody>
          {/* header */}
          <h5 className="text-center border-bottom border-1 pb-3 mb-3 fw-bold">
            {t("عناصر الفحص")}
          </h5>
          {/* body */}

          <form onSubmit={handleFormSubmit}>
            <Stack gap={1}>
              {cachedBasicData?.examination_items?.map((arrField, index) => {
                return (
                  <Label
                    key={arrField.id}
                    htmlFor={`items.${index}.house_examination_item_id`}
                    className="border p-2 rounded"
                  >
                    <Row>
                      <Col xs={11}>
                        <span>
                          {getLocalizedText(
                            cachedBasicData.examination_items[index].name_ar,
                            cachedBasicData.examination_items[index].name_en
                          )}
                        </span>
                      </Col>
                      <Col xs={1}>
                        <Controller
                          control={form.control}
                          name={`items.${index}`}
                          render={({ field }) => {
                            return (
                              <Checkbox
                                style={{ display: isTrack ? "none" : "block" }}
                                className="square-checkbox"
                                {...field}
                                id={`items.${index}.house_examination_item_id`}
                                type="checkbox"
                                onChange={(e) => {
                                  field.onChange(
                                    e.target.checked
                                      ? cachedBasicData.examination_items[index]
                                        .id
                                      : null
                                  );
                                }}
                                checked={
                                  field.value ===
                                  cachedBasicData.examination_items[index].id
                                }
                              />
                            );
                          }}
                        />
                      </Col>
                    </Row>
                  </Label>
                );
              })}
            </Stack>
            {/* footer */}
            <ExaminationItemsFooter
              onClose={onClose}
              handleFormSubmit={handleFormSubmit}
            />
          </form>
        </ModalBody>
      </Modal>
    </FormProvider>
  );
};

const ExaminationItemsFooter = (props) => {
  const { t } = useTranslation();
  const { onClose, handleFormSubmit } = props;
  const form = useFormContext();

  const watchedItems = useWatch({
    control: form.control,
    name: "items",
  });

  return (
    <Stack gap={2} className="sticky-bottom bg-white">
      <Divider />
      <Flex gap={4} justify="start" align="center">
        <Button
          disabled={!watchedItems?.some?.((item) => !!item)}
          className="btn btn-primary btn-xs"
          type="button"
          onClick={handleFormSubmit}
        >
          <HugeiconsIcon size={14} icon={FloppyDiskFreeIcons} />
          {t("SAVE")}
        </Button>
        <Button onClick={onClose} className="btn-danger btn-xs">
          <HugeiconsIcon size={14} icon={CancelIcon} />
          {t("CLOSE")}
        </Button>
      </Flex>
    </Stack>
  );
};
